import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateQuerySubSourceRequestSchema,
  type CreateQuerySubSourceRequest,
  type QuerySubSourceDetail,
  type QuerySubSourceListItem,
  UpdateQuerySubSourceRequestSchema,
  type UpdateQuerySubSourceRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCreateQuerySubSource,
  useQuerySourceOptions,
  useUpdateQuerySubSource,
} from '../use-query-sub-sources';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface QuerySubSourceFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  querySubSource?: QuerySubSourceDetail | QuerySubSourceListItem | null;
}

const CREATE_DEFAULTS: CreateQuerySubSourceRequest = {
  querySourceId: '',
  code: '',
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. The `querySourceId` select is driven by
 * the live options list from GET /query-sources/options - same slim shape used
 * by other sub-entity forms (branches, etc.).
 */
const QuerySubSourceFields = ({
  pending,
  sourceOptions,
}: {
  pending: boolean;
  sourceOptions: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element => (
  <>
    <UzFormField name="querySourceId">
      {({ field, error }) => (
        <UzSelect
          label="Query source"
          placeholder="Select parent source"
          value={(field.value as string | undefined) ?? ''}
          onValueChange={field.onChange}
          options={sourceOptions}
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Existing Customer"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive sub sources are hidden from lead forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const QuerySubSourceFormDrawer = ({
  open,
  onOpenChange,
  mode,
  querySubSource,
}: QuerySubSourceFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createQuerySubSource = useCreateQuerySubSource();
  const updateQuerySubSource = useUpdateQuerySubSource();
  const pending = createQuerySubSource.isPending || updateQuerySubSource.isPending;

  const sourceOptionsQuery = useQuerySourceOptions();
  const sourceOptions: ReadonlyArray<{ value: string; label: string }> = (
    sourceOptionsQuery.data ?? []
  ).map((s) => ({ value: s.id, label: s.name }));

  const createForm = useForm<CreateQuerySubSourceRequest>({
    resolver: zodResolver(CreateQuerySubSourceRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateQuerySubSourceRequest>({
    resolver: zodResolver(UpdateQuerySubSourceRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (querySubSource) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        querySourceId: querySubSource.querySource.id,
        name: querySubSource.name,
        description: querySubSource.description ?? '',
        sortOrder: querySubSource.sortOrder,
        isActive: querySubSource.isActive,
      });
    }
  }, [open, isCreate, querySubSource, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createQuerySubSource.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!querySubSource) return;
    await updateQuerySubSource.mutateAsync({
      id: querySubSource.id,
      payload: {
        ...values,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <QuerySubSourceFields pending={pending} sourceOptions={sourceOptions} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        isCreate ? 'Add query sub source' : `Edit ${querySubSource?.name ?? 'query sub source'}`
      }
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
